import { filter, map, sortBy } from 'lodash';
import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';

import {
  getValueFromURLParameters,
  getValuesFromStringQuery,
  removeSearchOptionFromStringQuery,
} from 'src/presentation/components/pages/shared/utils/Utils';

import RouterLinkButton from 'src/presentation/components/RouterLinkButton';

import { ButtonTypes } from '@ad-scripts/presentation/components/common/button';
import { isLoadingStatus } from 'src/presentation/hooks/useResource';
import { RootState } from 'src/actions/configuration/rootReducer';
import { StyledConfirmOrCancelModal } from 'src/presentation/components/pages/shared/rowActions/StyledConfirmOrCancelModal';
import analytics from '@ad-scripts/lib/analytics';
import EmbedModal from 'src/presentation/components/pages/shared/EmbedModal';
import { getCdn, getPreviewUrl } from './helpCenterHelpers';
import HelpCenterEditor from './HelpCenterEditor';
import helpCenterResource from 'src/actions/helpCenter/helpCenterResource';
import OverflowMenu, {
  OverflowMenuExternalLink,
  OverflowMenuItem,
  OverflowMenuLink,
  OverflowMenuText,
  usePopoverMenu,
} from 'src/presentation/components/pages/shared/OverflowMenu';
import TablePage, { TablePageRender } from '../shared/TablePage';
import Textarea from '@ad-scripts/presentation/components/common/textarea';
import useDocumentResource from 'src/presentation/hooks/useDocumentResource';

const columnConfig = [
  { attr: 'name', label: 'Name', defaultValue: 'Untitled' },
  { attr: 'lng', label: 'Language' },
  {
    attr: 'sections',
    label: 'Sections',
    customRenderer: item => map(item.sections, s => s.header).join(', ') || 'none',
  },
];

const HelpCenter: TablePageRender = ({ root }) => {
  const orgId = useSelector((state: RootState) => state.auth.orgId);
  const actions = (
    <RouterLinkButton data-aid={`addNew`} to={`${root}/new`}>
      Create Help Center
    </RouterLinkButton>
  );
  const { errors, loadingState, value } = useDocumentResource(helpCenterResource);
  const helpCenterConfigs = sortBy(value.siteConfigs, item => item.lng.toLowerCase());

  const [isEmbedModalVisible, setEmbedModalVisible] = useState(false);
  const [embedItem, setEmbedItem] = useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const onDelete = useOnDelete({ remove: helpCenterResource.effects.remove, itemId: deleteItem });

  const openDeleteModal = function (deleteItemId) {
    setDeleteItem(deleteItemId);
    setIsDeleteModalVisible(true);
  };
  const handleDeleteCancel = function () {
    setIsDeleteModalVisible(false);
  };
  const handleDeleteSubmit = function () {
    onDelete();
    setIsDeleteModalVisible(false);
  };
  const openModal = function () {
    setEmbedModalVisible(true);
  };
  const handleCancel = function () {
    setEmbedModalVisible(false);
  };

  const filterItems = (items, searchString) => {
    if (!searchString) {
      return items;
    }

    const desiredAudiences = getValuesFromStringQuery(searchString, 'audiences');
    const cleanSearch = removeSearchOptionFromStringQuery(searchString, 'audiences');

    const results = filter(items, item => {
      const hasDesiredAudience = !desiredAudiences.length || desiredAudiences.includes(item.audienceId);
      return hasDesiredAudience && item.name.toLowerCase().indexOf(cleanSearch.toLowerCase()) > -1;
    });
    return results;
  };
  const renderRowActions = ({ item }) => {
    return (
      <HelpCenterActions
        openDeleteModal={openDeleteModal}
        openModal={openModal}
        item={item}
        root={root}
        orgId={orgId}
        setEmbedItem={setEmbedItem}
      />
    );
  };

  const audienceParam = getValueFromURLParameters('audiences');
  const initialSearchString = audienceParam ? `audiences:${audienceParam}` : '';
  const modalContent = getModalContent(orgId, embedItem);
  return (
    <>
      <TablePage
        actions={actions}
        columnConfig={columnConfig}
        editor={HelpCenterEditor}
        errors={errors}
        filterFunc={filterItems}
        initialSearchString={initialSearchString}
        isLoading={isLoadingStatus(loadingState)}
        items={helpCenterConfigs}
        name="Help Center"
        description={getDescription()}
        resetErrors={helpCenterResource.actions.resetErrors}
        root={root}
        renderRowActions={renderRowActions}
        data-aid="help-center-page"
      />
      {isEmbedModalVisible && (
        <EmbedModal title="Embed Your Help Center" content={modalContent} onCancel={handleCancel} />
      )}
      {isDeleteModalVisible ? (
        <StyledConfirmOrCancelModal
          data-aid="delete-helpCenter"
          onCancel={handleDeleteCancel}
          onSubmit={handleDeleteSubmit}
          submitButtonType={ButtonTypes.DANGER}
          submitText="Delete Help Center"
          text={
            <>
              Any webpage using this Help Center will no longer work
              <br />
              once this Help Center is deleted.
            </>
          }
          title="Delete this Help Center?"
        />
      ) : null}
    </>
  );
};

function HelpCenterActions({ item, openModal, openDeleteModal, root, setEmbedItem, orgId }) {
  const { onClose, ...overFlowProps } = usePopoverMenu();

  const previewUrl = getPreviewUrl(item.id, orgId);

  return (
    <OverflowMenu id={item.id} onClose={onClose} {...overFlowProps}>
      <OverflowMenuItem id="edit">
        <OverflowMenuLink to={`${root}/${item.id}`}>Edit Help Center</OverflowMenuLink>
      </OverflowMenuItem>
      <OverflowMenuItem id="sections">
        <OverflowMenuLink to={`${root}/${item.id}/sections`}>Create/Edit Sections</OverflowMenuLink>
      </OverflowMenuItem>
      <OverflowMenuItem id="preview-layout">
        <OverflowMenuExternalLink
          href={previewUrl}
          target="_blank"
          onClick={() => {
            analytics.track('Admin - Help Center Layout Viewed', { helpCenterId: item.id });
            return true;
          }}
        >
          View Layout
        </OverflowMenuExternalLink>
      </OverflowMenuItem>
      <OverflowMenuItem id="embed">
        <OverflowMenuLink
          to="#"
          onClick={() => {
            onClose();
            setEmbedItem(item.id);
            openModal();
          }}
        >
          Embed
        </OverflowMenuLink>
      </OverflowMenuItem>
      <OverflowMenuItem
        id="delete"
        to="#"
        onClick={() => {
          onClose();
          openDeleteModal(item.id);
        }}
      >
        <OverflowMenuText>Delete</OverflowMenuText>
      </OverflowMenuItem>
    </OverflowMenu>
  );
}

const getDescription = () => (
  <span>
    A Help Center allows your customers to view Public Answers directly on your website. Each Help Center has a code
    snippet that you&apos;ll include on your website. See our{' '}
    <a target="_blank" rel="noopener noreferrer" href="https://developer.gladly.com/help-center/">
      Developer Site
    </a>{' '}
    for our implementation guide and our{' '}
    <a target="_blank" rel="noopener noreferrer" href="https://help.gladly.com/docs/help-center-configuration/">
      Help Site
    </a>{' '}
    for details on how to configure your Help Center.
  </span>
);

export function useOnDelete({ remove, itemId }) {
  const dispatch = useDispatch();
  return useCallback(() => {
    dispatch(remove(itemId));
  }, [dispatch, itemId, remove]);
}

function getModalContent(orgId, embedItem) {
  const snippet = `
<script type="text/javascript">
  window.gladlyHCConfig = { api: '${
    window.location.origin
  }', orgId: '${orgId}', brandId: '${embedItem}', cdn: '${getCdn()}', selector: '#gladly-help-center' };
</script>
<script type="text/javascript">
  function l() {
    var t = document,
      e = t.createElement('script');
    (e.type = 'text/javascript'), (e.async = !0), (e.src = 'https://cdn.gladly.com/help-center/hcl.js');
    var a = t.getElementsByTagName('script')[0];
    a.parentNode.insertBefore(e, a);
  }
  var w = window;
  w.attachEvent ? w.attachEvent('onload', l) : w.addEventListener('DOMContentLoaded', l, !1);
</script>`;

  return (
    <div>
      <p>
        Copy this code snippet to the web page where you would like the Help Center to live. An HTML element with
        id=&quot;gladly-help-center&quot; must exist in order for the Gladly Help Center to load.
      </p>
      <Code value={snippet.trim()} />
      <p>
        For more information about embedding a Help Center, head to{' '}
        <a rel="noopener noreferrer" target="_blank" href="https://developer.gladly.com/help-center/">
          https://developer.gladly.com/help-center/
        </a>
        .
      </p>
    </div>
  );
}

const Code = styled(Textarea)`
  font-family: 'Courier New', Courier, monospace;
  white-space: pre;
`;

export default HelpCenter;
